import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  NgModule,

  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FFacetInfoModule } from '@wipo/w-angular/internal';
import { WInputCheckboxOneModule } from '@wipo/w-angular/w-input-checkbox-one';
import { FormsModule } from '@angular/forms';
import { window, WUniversalModule, WAdaptiveBase, compareObjectArraysInOrder, compareSimpleArraysInOrder } from '@wipo/w-angular/shared';
import { BehaviorSubject, from, interval, merge } from 'rxjs';
import { debounceTime, mergeMap, switchMap, take, takeUntil, tap, throttleTime } from 'rxjs/operators';
import { WHideEmptyModule, WLazyLoadable, W_LAZY_LOADABLE, observeIntersection } from '@wipo/w-angular/directives';
import { FacetWViewPanelComponent } from './facet-w-view-panel.component';


/** state of folded/unfolded facet set */
interface FacetSetState {
  sectionContent: boolean;
  section: boolean;
  footer: boolean;
}
const ATTENTION_OPTIONS = ['warning', 'error', 'important', 'success'];

@Component({
  selector: 'w-view-panel',
  styleUrls: ['./w-view-panel.component.scss'],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './w-view-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'class': 'b-view-panel--is-ulf',
    '[class.b-view-panel--legacy]': 'legacy',
    '[class.b-view-panel--is-edit-denied]': 'isDisabled',
    '[class.b-view-panel--has-warning]': 'type === "warning"',
    '[class.b-view-panel--has-error]': 'type === "error"',
    '[class.b-view-panel--has-highlight]': 'type === "important"',
    '[class.b-view-panel--has-success]': 'type === "success"',
    '[class.b-view-panel--selected]': 'selected',
    '[class.b-view-panel--is-folded-out]': 'hasHingeOf(0) && hasFoldedHinge(false, 0)',
    '[class.b-view-panel__with-hinge]': 'hasHingeOf(0) && !noHinge',
    '[style.min-height.px]': 'minHeightWhenInitialized',
    '[attr.content-light]': 'emphasis === "light" ? "true" : null',
    '[attr.content-bold]': 'emphasis === "bold" ? "true" : null',
  },
  // if we provide W_LAZY_LOADABLE, lazyLoading directive will update component's visibility status
  providers: [
    { provide: W_LAZY_LOADABLE, useExisting: forwardRef(() => WViewPanelComponent) },
  ],
  standalone: false
})
/*
@id w-view-panel
@name w-view-panel
@status release
@description Use "type" input to indicate warning, error, important, success (literal text)
*/
export class WViewPanelComponent extends WAdaptiveBase implements OnInit, AfterViewInit, OnChanges, WLazyLoadable {
  @ContentChildren(FacetWViewPanelComponent) panels!: QueryList<FacetWViewPanelComponent>;

  @ViewChildren('foldedSectionContent') foldedSectionContents!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('unfoldedSectionContent') unfoldedSectionContents!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('place') placeholders!: QueryList<ElementRef<HTMLElement>>; // observe by intersection observer
  @ViewChildren('foldedSection') foldedSections!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('foldedFooter') foldedFooters!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('unfoldedSection') unfoldedSections!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('unfoldedFooter') unfoldedFooters!: QueryList<ElementRef<HTMLElement>>;
  @ViewChildren('primaryAction') primaryActions!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('freeAction') freeActions!: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('actionsSection') actionsSections!: QueryList<ElementRef<HTMLDivElement>>;
  /** contains multiple panel */
  private get isMulti() {
    return (this.panels?.length ?? 0) > 0;
  }


  @Input() title?: string; // view panel title (HTML)
  @Input() disableUppercase = false; // do not force title uppercase (manage looks with HTML)

  @Input() reviewMode: boolean = false; // @deprecated hide action buttons in "unfolded" state
  @Input() ignoreReviewMode: boolean = false; // @deprecated override review mode
  @Input() disabled: boolean = false; // @deprecated hide action buttons

  /** can be used per internal panel */
  @Input() highlighted: boolean = false; // use special background color
  @Input() type: string = '';  // @options[warning,error,important,success,<empty string>]
  @Input() emphasis: string = '';  // @options[bold,light,<empty string>] shows content bold or light
  @Input() folded = true; // set current state
  @Input() selected: boolean = false; // selected value
  @Input() facets?: string[]; // dynamically list facets to show, if empty, available facets evaluated at runtime; when present "folded" parameter ignored
  @Input() legacy = false; // use legacy white background

  @Input() selectable: boolean = false; // show checkbox (cannot be used with hinge)
  @Input() minWidthInGrid?: number;  // (px) use when displayed in grid to control number of panels per row
  @Input('no-hinge') _noHinge?: string; // fold/unfold by clicking on all surface

  @Input() outOfViewportHeight: number = 100; // numeric value, height in px of the placeholder panel when out of viewport (component itself will build when scrolls in viewport)
  // when user sets outOfViewportHeight it triggers lazy loading and sets (else we handle ourselves)
  isLazyLoading: boolean = false; // set to true when handled by directive
  isVisible: boolean = false; // we set to true as soon as visible. And do not reset even when out ofviewport again
  isInitialized = false;
  visibilityChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  get minHeightWhenInitialized() {
    return this.isInitialized ? 0 : this.outOfViewportHeight ?? 0 // if not initialized, use placeholder height. unless
  }

  @Output() selectedChange = new EventEmitter<boolean>(); // selection change
  @Output() wFolded = new EventEmitter<boolean>(); // folded/unfolded status
  @Output() wGridColumns = new EventEmitter<number>(); // emits when number of elements in grid row change
  @Output() wVisible = new EventEmitter<boolean>(true); // emits true when component first becomes visible (scrolls in viewport), fires at once if outOfViewportHeight set to 0

  F = {
    ACTION_SECONDA: 'secondary-actions', ACTION_PRIMA: 'primary-action', ACTION_FREE: 'free-action',
    HINGE: 'hinge',
    SECTION_FOLDED: 'section-folded', FOLDED: 'folded',
    SECTION_UNFOLDED: 'section-unfolded', UNFOLDED: 'unfolded',
    TITLE: 'title', INFO: 'info',
    FOOTER_FOLDED: 'footer-folded', FOOTER_UNFOLDED: 'footer-unfolded'
  }
  minHeight = '0'; // calculated if text button in primary section
  minHeights = ['0'];
  inViewports: boolean[] = [false];

  @HostBinding('class') /** single panel, use class on host */
  get c() {
    return this.isMulti ? '' : this.mainClass;
  }
  /** used by internal wrapper */
  get mainClass() {
    return `b-view-panel ${(this.minWidthInGrid ? "b-grid-item--" + this.gridColumns : "")}`;
  }
  outOfViewportHeightOf(i: number) {
    const target = this.panels.get(i) || this;
    return (target.outOfViewportHeight ?? 0)
  }
  usePlaceholderOf(i: number) {
    const target = this.panels.get(i) || this;
    return (target.outOfViewportHeight ?? 0) > 0 && !this.inViewportOf(i);
  }
  hingeIcon(i: number) {
    return this.hasFoldedHinge(true, i) ? 'chevron_right' : 'expand_more';
  }
  hasFoldedHinge(folded: boolean, i: number) {
    // /** target particular panel */
    return folded ?
      (this.panels.get(i) ?? this).folded && (this.hasUnfoldedOf(i) || this.showFacet(this.F.HINGE, i, false)) : // folded and has unfolded facets (or hinge explicitly set)
      !(this.panels.get(i) ?? this).folded && (this.hasFoldedOf(i) || this.showFacet(this.F.HINGE, i, false));
  }

  /** can fold if either folded or section-folded (section content)  exists and has either children or produces text */
  hasHingeOf(i: number) {
    return this.showFacet(this.F.HINGE, i, false) || // explicitly force hinge in fixed mode
      this.hasFoldedHinge(true, i) ||  // has unfolded facets and current state is folded or we use fixed facets
      this.hasFoldedHinge(false, i) ||  // has folded facets and current state is unfolded or we use fixed facets
      this.hasProblem(i);
  }
  private hasFacetContent(el: HTMLElement): boolean {
    return !!el?.children.length || !!el?.textContent;
  }

  hasFoldedOf(i: number, includeFooter = true): boolean {
    return this.foldedFacetSetStates[i]?.sectionContent || this.foldedFacetSetStates[i]?.section || includeFooter && this.foldedFacetSetStates[i]?.footer;
  }
  /** can unfold if either facet unfolded or section-unfolded (section content) exists and 
   * has either children or produces text 
   * we have also footer, but we display it unconditionally - whether it has content or not
  */
  hasUnfoldedOf(i: number, includeFooter = true): boolean {
    return this.unfoldedFacetSetStates[i]?.sectionContent || this.unfoldedFacetSetStates[i]?.section || includeFooter && this.unfoldedFacetSetStates[i]?.footer;
  }
  showFoldedOf(i: number, includeFooter = true): boolean {
    return ((this.panels.get(i) ?? this).folded ?? false) && this.hasFoldedOf(i, includeFooter);
  }
  showUnfoldedOf(i: number, includeFooter = true): boolean {
    return !(this.panels.get(i) ?? this).folded && this.hasUnfoldedOf(i, includeFooter);
  }
  selectorCountOf(i: number): number {
    let count = 0;
    if (this.hasHingeOf(i)) { count++; }
    if (this.selectable) { count++; }
    return count;
  }

  get noHinge() {
    return this._noHinge === '';
  }

  gridColumns = 3; // used to set size depending on parent and gridMinInWidth

  /** periodically update folded/unfolded state */
  refreshContentState = new EventEmitter<void>();
  // for panel set we build an array of facet states
  foldedFacetSetStates: FacetSetState[] = [];
  unfoldedFacetSetStates: FacetSetState[] = [];
  showTops: boolean[] = [];

  inViewportOf(i: number) {
    /** we overload first array element; but we might need to know context */
    return !this.isMulti && i === 0 ? this.isVisible : (this.inViewports[i] ?? false);
  }

  useFixedFacets = false;
  /** old fixed */
  _show_facet: Record<string, boolean> = {};
  constructor(protected elementRef: ElementRef<HTMLElement>, private cdr: ChangeDetectorRef) {
    super();
    // set initial height, so that stack of panels does not try to be built at once, max 4 panels
    const height = (window.innerHeight || document.documentElement.clientHeight);
    // this will be set to 0 when loaded, then we see if it is in viewport or not
    elementRef.nativeElement.setAttribute('style', `min-height: ${height / 4}px`);
  }
  id = `x-${Math.random() * 100000}`;
  override ngOnInit(): void {
    super.ngOnInit();
    const rect = this.elementRef.nativeElement.getBoundingClientRect();
    this.isVisible = rect.top < (window.innerHeight || document.documentElement.clientHeight); // initial state
    this.cdr.markForCheck();

    // re-try few times in case the page is slow
    interval(100).pipe(take(4)).subscribe((i) => this.captureWidth());
    this.fromEvent(window, 'resize').subscribe(this.captureWidth.bind(this));
    /** cannot figure out how to do this cleanly, so add interval to pick up any changes not explicitly notified */
    const changes = merge(this.refreshContentState, this.wFolded, interval(100))
      .pipe(
        takeUntil(this.destruction)
      );
    // debounce triggers 50ms after last event, throttle triggers every 50ms, well, we get 2 events...s
    merge(
      changes.pipe(debounceTime(50)),
      changes.pipe(throttleTime(50)),
    )
      .subscribe(() => this.delayFoldedState());
  }
  ngOnChanges(changes: SimpleChanges): void {
    /** if we set "facets" we control what is shown (using *ngIf), 
     * else we expect all facets to be available, detect on the fly and show/hide as necessary 
     * */
    const facets = changes['facets'];
    if (facets && !compareSimpleArraysInOrder(facets.currentValue, facets.previousValue)) {
      this._show_facet = (this.facets || []).reduce((acc, f) => ({ ...acc, [f]: true }), {} as Record<string, boolean>);
      this.useFixedFacets = (this.facets?.length ?? 0) > 0;
      this.cdr.markForCheck();
      this.refreshContentState.emit();
    }
  }
  ngAfterViewInit(): void {
    /** on changes trigger state update */
    merge(this.foldedSectionContents.changes, this.unfoldedSectionContents.changes, this.foldedSections.changes, this.unfoldedSections.changes,
      this.foldedFooters.changes, this.unfoldedFooters.changes, this.primaryActions.changes, this.freeActions.changes, this.actionsSections.changes).pipe(takeUntil(this.destruction))
      .subscribe(() => this.delayFoldedState());

    if (!this.isMulti) {
      // single panel, let lazyLoading directive do the job
      this.visibilityChange.pipe(takeUntil(this.destruction)).subscribe((isVisible) => {
        this.inViewports[0] = isVisible;
        this.isVisible = true;
        this.wVisible.next(true);
        // build template
        this.cdr.detectChanges();
        // measure what we got
        this.delayFoldedState();
      });
      if (!this.isLazyLoading) {
        // we need to handle ourselves
        if ((this.outOfViewportHeight ?? 0) > 0) {
          observeIntersection((isVisible: boolean) => this.visibilityChange.emit(isVisible), this.elementRef.nativeElement);
        } else {
          this.visibilityChange.emit(true); // no placeholder height, always visible
        }
      }
    }
    /** watch panels */
    const panels = new BehaviorSubject(this.panels.toArray());
    panels.pipe(
      takeUntil(this.destruction),
      switchMap((p) =>
        from(p).pipe(
          mergeMap(t => t.wVisible.pipe(
            takeUntil(this.destruction),
            takeUntil(this.panels.changes),
            tap(() => {
              this.refreshContentState.emit();
              this.cdr.markForCheck();
            })
          )))
      )
    ).subscribe();
    /** if panels change the prvious subscription stops */
    this.panels.changes.pipe(takeUntil(this.destruction)).subscribe(() => panels.next(this.panels.toArray()));
    /* check state as-is */
    this.delayFoldedState();
    /** init visibility/availability flags */
    this.initComplex();
    setTimeout(() => {
      this.isInitialized = true;
      this.cdr.markForCheck();
    })

    // this.start = Date.now(); this.cnt = 0;// for debugging purposes only
  }

  private initComplex() {
    // check presence of actions
    this.adjustActions();
    if (this.isMulti) {
      // observe placeholders
      this.inViewports = this.panels.map((p, i) => p.outOfViewportHeight === undefined);
      // control embedded panels visibility
      this.placeholders.forEach((p, i) => {
        const el = p.nativeElement as Element;
        const idx: number = parseInt(el.getAttribute('data-index') ?? '0', 10);
        const panel = this.panels.get(idx);
        if (panel) {
          if (!this.inViewports[i]) {
            observeIntersection((isVisible: boolean) => {
              this.inViewports[idx] = isVisible;
              panel.visibilityChange.emit(isVisible);
              this.cdr.markForCheck(); // mark for check to update view
            }, el);
          } else {
            panel.visibilityChange.emit(true);
          }
        }
      });
      this.panels.forEach((p, i) => {
        p.wUpdated.pipe(takeUntil(this.destruction))
          .subscribe(() => {
            this.cdr.markForCheck();
            this.refreshContentState.emit();
          });
      }); // mark for check to update view
    } else {
      // single panel
      this.inViewports = [this.isVisible]; // single panel, no placeholders, no need to observe anything
    }
    this.refreshContentState.emit();
  }
  showFacet(f: string | string[], i: number, defaultStateIfFacetsNotSpecified = true): boolean {
    const target = this.panels.get(i) || this;
    return (!target.useFixedFacets && defaultStateIfFacetsNotSpecified) || (typeof f === 'string' ? [f] : f).some(x => !!target._show_facet[x]);
  }
  private hasTextButton(facet?: ElementRef<HTMLDivElement>) {
    return facet ? Array.from(facet.nativeElement.children).some(c =>
      !c.hasAttribute('icon-button')) : false;
  }
  private adjustActions() {
    setTimeout(() => {
      // guess min height of panel
      if (this.primaryActions.length > 0) {
        const heights = this.primaryActions.toArray().map((a, i) => {
          const hasTextButton = this.hasTextButton(a) || this.hasTextButton(this.freeActions.get(i));
          // if we have (small) text button, force minimum size
          return !this.isMobile && hasTextButton ? '72px' : '0';
        })
        if (!compareSimpleArraysInOrder(heights, this.minHeights)) {
          this.minHeights = heights;
          this.cdr.markForCheck();
        }
      }
      // deal with multiple panels
      if (this.actionsSections.length > 0) {
        this.actionsSections.forEach((a, i) => {
          const width = a.nativeElement.clientWidth;
          a.nativeElement.style.setProperty('--actions-width', `${width}px`);
          this.cdr.markForCheck();
        })
      }
    });
  }
  /** we call this if wFolded emits or component appears in viewport */
  private delayFoldedState() {
    /** multiple panels */
    /** each facet set might miss some items, so we need to check the data-index attribute for the correct index */
    const stateLength = this.isMulti ? this.panels.length : 1;
    let updated = false;
    /** folded**/
    const foldedFacetSetStates = transposeArray([
      this.hasFacetContentArray(this.foldedSectionContents, stateLength),
      this.hasFacetContentArray(this.foldedSections, stateLength),
      this.hasFacetContentArray(this.foldedFooters, stateLength)]).map(x => (
        { sectionContent: x[0], section: x[1], footer: x[2] }));

    updated ||= !compareObjectArraysInOrder(foldedFacetSetStates, this.foldedFacetSetStates);


    const unfoldedFacetSetStates = transposeArray([
      this.hasFacetContentArray(this.unfoldedSectionContents, stateLength),
      this.hasFacetContentArray(this.unfoldedSections, stateLength),
      this.hasFacetContentArray(this.unfoldedFooters, stateLength)]).map(x => (
        { sectionContent: x[0], section: x[1], footer: x[2] }));

    updated ||= !compareObjectArraysInOrder(unfoldedFacetSetStates, this.unfoldedFacetSetStates);

    /** check if any of top facets are present */
    const showTops = [...this.showTops];
    const foldedFacets = [this.F.SECTION_FOLDED, this.F.FOLDED]; // , this.F.ACTION_SECONDA, this.F.ACTION_PRIMA, this.F.ACTION_FREE];
    const unfoldedFacets = [this.F.SECTION_UNFOLDED, this.F.UNFOLDED]; //, this.F.ACTION_SECONDA, this.F.ACTION_PRIMA, this.F.ACTION_FREE];
    if (this.isMulti) {
      this.panels.forEach((panel, i) => {
        showTops[i] = panel.folded && this.showFoldedOf(i, false) ||
          this.showFacet(foldedFacets, i, false) ||
          !panel.folded && this.showUnfoldedOf(i, false) ||
          this.showFacet(unfoldedFacets, i, false);
      });
    } else {
      // single panel
      const i = 0;
      showTops[i] = this.folded && this.showFoldedOf(i, false) || this.showFacet(foldedFacets, i, false) ||
        !this.folded && this.showUnfoldedOf(i, false) || this.showFacet(unfoldedFacets, i, false);
    }
    const changedTops = !compareSimpleArraysInOrder(this.showTops, showTops);
    if (changedTops) {
      updated = true;
    }

    if (updated) {
      setTimeout(() => {
        this.foldedFacetSetStates = foldedFacetSetStates;
        this.unfoldedFacetSetStates = unfoldedFacetSetStates;
        this.showTops = showTops;
        this.cdr.detectChanges();
      });
    }
  }
  private hasFacetContentArray(elements: QueryList<ElementRef<HTMLElement>>, length: number): boolean[] {
    if (!elements) return [];
    const allFalseArray = Array(length).fill(false);
    elements.forEach((el) => {
      const index = parseInt(el.nativeElement.getAttribute('data-index') ?? '0', 10) || 0;
      if (this.hasFacetContent(el.nativeElement)) allFalseArray[index] = true;
    })
    return allFalseArray;
  }
  private captureWidth() {
    if (this.minWidthInGrid) {
      const parentWidth = this.elementRef.nativeElement.parentElement?.clientWidth ?? 0;
      const cols = Math.floor(parentWidth / this.minWidthInGrid) || 1;
      if (cols !== this.gridColumns) {
        this.gridColumns = cols;
        this.wGridColumns.emit(this.gridColumns);
      }
    }
    this.adjustActions();
  }
  toggle(i: number, card?: boolean, event?: Event) {
    // card if whole surface clicked
    const selected = window.getSelection().type === 'Range';
    if (!card || (this.noHinge && !selected)) {
      const target = this.panels.get(i) || this;
      const state = !target.folded;
      target.folded = state;
      target.wFolded.emit(state);
      event?.preventDefault(); // prevent scroll
    }
  }
  toggleSelection(i: number, state: boolean) {
    if (state !== undefined) {
      const target = this.panels.get(i) || this;
      target.selected = state;
      target.selectedChange.emit(state);
    }
  }
  clickSelector(event: Event) {
    // do not reach toggle CARD if we clicked on checkbox
    event.stopPropagation();
  }
  get isReviewMode(): boolean {
    return this.reviewMode && !this.ignoreReviewMode;
  }

  get isDisabled(): boolean {
    return this.disabled || this.isReviewMode;
  }

  hasProblem(i: number): boolean {
    return ATTENTION_OPTIONS.includes((this.panels.get(i) ?? this).type);
  }
}
const transposeArray = <T>(array: T[][]): T[][] => {
  const numRows = array.length;
  const numCols = array[0].length;

  // Create a new array with dimensions swapped
  const transposedArray: T[][] = Array(numCols)
    .fill(null)
    .map(() => Array(numRows));

  // Iterate through the original array and populate the transposed array
  for (let i = 0; i < numRows; i++) {
    for (let j = 0; j < numCols; j++) {
      transposedArray[j][i] = array[i][j];
    }
  }

  return transposedArray;
}



@NgModule({
  imports: [CommonModule, FormsModule, FFacetInfoModule, WInputCheckboxOneModule, WHideEmptyModule, WUniversalModule],
  exports: [WViewPanelComponent, FacetWViewPanelComponent],
  declarations: [WViewPanelComponent, FacetWViewPanelComponent]
})
export class WViewPanelModule { }